import { useAuth0 } from '@auth0/auth0-react'
import { useEffect } from 'react'

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'

export interface User {
  userId: string
  email: string
  firstName: string
  lastName: string
  mobile?: string
  joinedYear: string
  role: string
  isApproved: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
  bio?: string
  avatar?: string
  attendanceYears?: string[]
}

export interface UpdateUserData {
  firstName?: string
  lastName?: string
  email?: string
  mobile?: string
  joinedYear?: string
  attendanceYears?: string[]
  bio?: string
  avatar?: string
}

class UsersApi {
  private getAccessTokenSilently: (() => Promise<string>) | null = null

  setAuth(getAccessTokenSilently: () => Promise<string>) {
    this.getAccessTokenSilently = getAccessTokenSilently
  }

  private async getHeaders(): Promise<HeadersInit> {
    if (!this.getAccessTokenSilently) {
      throw new Error('Auth not initialized. Call setAuth() first.')
    }
    
    try {
      const token = await this.getAccessTokenSilently()
      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    } catch (error) {
      throw new Error('Authentication failed')
    }
  }

  async getCurrentUser(): Promise<User> {
    try {
      const headers = await this.getHeaders()
      const user = await this.getAccessTokenSilently?.()
        .then(token => {
          try {
            const base64Url = token.split('.')[1]
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => 
              '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
            ).join(''))
            return JSON.parse(jsonPayload)
          } catch (error) {
            return null
          }
        })
        .catch(() => null)

      const response = await fetch(`${API_BASE_URL}/me`, {
        method: 'GET',
        headers,
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      if (!data || !data.userId) {
        throw new Error('Invalid user data in response')
      }

      // Transform the data to match our User interface
      const status = (data.status || '').toLowerCase()
      const transformedData = {
        ...data,
        email: data.email || user?.email || '',  // Use Auth0 email as fallback
        userId: data.userId || user?.sub || '',  // Use Auth0 sub as fallback
        isApproved: status === 'active' || status === 'approved' || status === 'yes',
        isActive: status === 'active' || status === 'approved' || status === 'yes',
        createdAt: data.createdAt || data.updatedAt || new Date().toISOString(),
        updatedAt: data.updatedAt || new Date().toISOString(),
        joinedYear: data.joinedYear,
        attendanceYears: Array.isArray(data.attendanceYears) ? data.attendanceYears : []
      }

      return transformedData
    } catch (error) {
      throw error
    }
  }

  async updateProfile(data: UpdateUserData & { joinedYear?: string }): Promise<User> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(`${API_BASE_URL}/me`, {
        method: 'PATCH',
        headers,
        credentials: 'include',
        body: JSON.stringify(data)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }

      const responseData = await response.json()
      // Transform the response data to match our User interface
      const status = (responseData.user?.status || '').toLowerCase()
      const transformedData = {
        ...responseData.user, // The backend returns { user: {...} }
        isApproved: status === 'active' || status === 'approved' || status === 'yes',
        isActive: status === 'active' || status === 'approved' || status === 'yes',
        createdAt: responseData.user.createdAt || responseData.user.updatedAt || new Date().toISOString(),
        updatedAt: responseData.user.updatedAt || new Date().toISOString(),
        joinedYear: responseData.user.joinedYear,
        attendanceYears: Array.isArray(responseData.user.attendanceYears) ? responseData.user.attendanceYears : []
      }
      return transformedData
    } catch (error) {
      throw error
    }
  }

  async getUsers(): Promise<{ users: User[]; total: number }> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(`${API_BASE_URL}/users`, {
        method: 'GET',
        headers,
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      // Transform each user to match our User interface
      const transformedUsers = data.users.map((user: any) => ({
        ...user,
        userId: user.userId || user.sub || '',  // Add fallback for userId
        isApproved: user.status === 'active' || user.status === 'approved',
        isActive: user.status === 'active' || user.status === 'approved',
        createdAt: user.createdAt || user.updatedAt || new Date().toISOString(),
        updatedAt: user.updatedAt || new Date().toISOString(),
        joinedYear: user.joinedYear,
        attendanceYears: Array.isArray(user.attendanceYears) ? user.attendanceYears : [],
        name: user.name || `${user.firstName} ${user.lastName}`.trim(),
        role: user.role || 'user',
        status: user.status || 'approved'
      }))

      return { users: transformedUsers, total: data.users.length }
    } catch (error) {
      throw error
    }
  }

  async getAdminStats(): Promise<{
    totalUsers: number
    pendingUsers: number
    totalEvents: number
    activePools: number
    totalRevenue: number
  }> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(`${API_BASE_URL}/admin/stats`, {
        method: 'GET',
        headers,
        credentials: 'include',
      })

      if (response.ok) {
        return response.json()
      }
    } catch {
      // Fall through to client-side estimate
    }

    const { users } = await this.getUsers()
    return {
      totalUsers: users.length,
      pendingUsers: users.filter((u: any) => u.status === 'pending').length,
      totalEvents: 0,
      activePools: 0,
      totalRevenue: 0,
    }
  }

  async getUser(userId: string): Promise<User> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(`${API_BASE_URL}/user/${userId}`, {
        method: 'GET',
        headers,
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      if (!data.users || !data.users[0] || !data.users[0].userId) {
        throw new Error('Invalid user data received from API')
      }
      
      // Transform the user data to match our User interface
      const user = data.users[0]
      return {
        ...user,
        userId: user.userId || user.sub || '',  // Add fallback for userId
        isApproved: user.status === 'active' || user.status === 'approved',
        isActive: user.status === 'active' || user.status === 'approved',
        createdAt: user.createdAt || user.updatedAt || new Date().toISOString(),
        updatedAt: user.updatedAt || new Date().toISOString(),
        joinedYear: user.joinedYear,
        attendanceYears: Array.isArray(user.attendanceYears) ? user.attendanceYears : [],
        name: user.name || `${user.firstName} ${user.lastName}`.trim(),
        role: user.role || 'user',
        status: user.status || 'approved'
      }
    } catch (error) {
      throw error
    }
  }

  async updateUser(userId: string, data: UpdateUserData): Promise<User> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(`${API_BASE_URL}/user/${userId}`, {
        method: 'PATCH',
        headers,
        credentials: 'include',
        body: JSON.stringify(data)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }

      const responseData = await response.json()
      return responseData.user
    } catch (error) {
      throw error
    }
  }

  async approveUser(userId: string): Promise<{ message: string; user: any; auth0?: any; note?: string }> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/users/${encodeURIComponent(userId)}/approve`, {
      method: 'POST',
      headers,
      credentials: 'include',
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  async denyUser(userId: string): Promise<{ message: string; user: any; auth0?: any }> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/users/${encodeURIComponent(userId)}/deny`, {
      method: 'POST',
      headers,
      credentials: 'include',
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  async deleteUser(userId: string): Promise<{ message: string; user: any; note?: string }> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/user/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
      headers,
      credentials: 'include',
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    return response.json()
  }
}

export const usersApi = new UsersApi()

// Hook to initialize usersApi with Auth0
export const useUsersApi = () => {
  const { getAccessTokenSilently, isLoading } = useAuth0()
  
  useEffect(() => {
    if (!isLoading) {
      usersApi.setAuth(getAccessTokenSilently)
    }
  }, [isLoading, getAccessTokenSilently])
  
  return usersApi
}